/* ALPHX Find Your Fit — %-stretch engine + style-aware thigh guidance
   Version: 2026-07-22b

   WHAT CHANGED FROM 2026-07-22a
   -----------------------------
   1. DATA MODEL: replaced the four flat inch thresholds per size with the
      real spec sheet — ONE garment measurement per size/line for waist and
      for thigh (the "doubled" flat-circumference numbers from your QC
      sheet). Everything else (relaxed/optimal/secure/max) is now computed
      as % stretch off that single number, using the universal bands you
      defined:
        WAIST: relaxed 0-9%, optimal 10-20%, secure 20-39%, max 40%+
        THIGH: relaxed 0-19%, optimal 20-35%, secure 36-49%, max 50%+
      This is the same math you were doing by hand on the Boxer Briefs
      31"/23" case — the engine now does it for every size/line automatically.
   2. RECOMMENDATION LOGIC: recommend() now checks the waist-matched size
      AND one size down / one size up, across every active line, and:
        - throws out anything that hits Max stretch on waist OR thigh
          (that's not "snug," it's not wearable — never recommend it)
        - ranks what's left by distance to the Optimal thigh band, so a
          Secure fit that's close to Optimal beats one that's deep in
          Secure territory, and Optimal always wins outright
      This directly reproduces the call you made manually on 31"/23":
      Medium Modern is disqualified (58.62%, over max), and among the
      three Secure options left, Large Athletic wins because 39.39% is
      closest to the Optimal line.
   3. MESSAGING: if the winning size differs from the waist-matched size,
      the result note says so plainly ("true waist size is M, recommending
      L for a better thigh fit") instead of silently swapping sizes.
   4. If literally nothing in the current lineup is wearable (both waist
      and thigh hit Max at every candidate size), recommend() returns
      size: null with a message pointing at what's coming rather than
      forcing a bad recommendation.
   5. UI: style question copy tightened, and the thigh question/hint now
      changes with the selected style, since each style is measured
      differently (see MEASURE_GUIDE below) — measuring a Trunk like a
      Boxer Brief (4.5" down instead of 2") would throw off every number
      above.
*/
(function () {
  // ---------- helpers ----------
  const WR = (min, max) => ({ min, max });
  const inRange = (v, min, max) => v >= min && v <= max;
  const ORDER = ["XS", "S", "M", "L", "XL", "XXL"];
  const sizeIndex = s => ORDER.indexOf(s);
  const sizeUp = s => (sizeIndex(s) < ORDER.length - 1 ? ORDER[sizeIndex(s) + 1] : null);

  // ---------- phase control ----------
  // Flip this ONE value to advance the 3-phase rollout. recommend() only
  // ever looks at lines listed for the active phase, and skips any line
  // that has no data yet for a given style/size.
  const ACTIVE_PHASE = "current"; // "current" | "phase1" | "phase2" | "phase3"

  const PHASE_LINES = {
    current: ["Modern", "Athletic"],
    phase1: ["Modern", "Athletic", "EDAthletic", "EDQuad"],
    phase2: ["Modern", "Athletic", "EDAthletic", "EDQuad", "EDModern"],
    phase3: ["EDModern", "EDAthletic", "EDQuad"]
  };

  // ---------- % stretch bands ----------
  // pct = (entered measurement - doubled spec) / doubled spec * 100
  const WAIST_BANDS = { relaxedMax: 9, optimalMax: 20, secureMax: 39 };
  const THIGH_BANDS = { relaxedMax: 19, optimalMax: 35, secureMax: 49 };

  function stretchPercent(entered, doubled) {
    return ((entered - doubled) / doubled) * 100;
  }

  function classify(pct, bands) {
    if (pct <= bands.relaxedMax) return "relaxed";
    if (pct <= bands.optimalMax) return "optimal";
    if (pct <= bands.secureMax) return "secure";
    return "max";
  }

  /** 0 if inside the Optimal band; otherwise how far outside it, in points. */
  function distanceToOptimal(pct, bands) {
    if (pct <= bands.relaxedMax) return bands.relaxedMax - pct;
    if (pct <= bands.optimalMax) return 0;
    if (pct <= bands.secureMax) return pct - bands.optimalMax;
    return Infinity;
  }

  // ---------- how each style should actually be measured ----------
  // Mismatched measurement method is a real failure mode: measuring a Trunk
  // 4.5" down (the Boxer Brief method) instead of 2" down will read a
  // different number than the spec sheet was built against.
  const MEASURE_GUIDE = {
  "Boxer Briefs": { method: "measured straight across", depth: "about 4.5\" down from your waistband" },
  "Trunks": { method: "measured straight across", depth: "about 2\" down from your waistband" },
  "Briefs": { method: "measured diagonally", depth: "about 2\" down from your waistband" },
  "Union Pants": { method: "measured straight across", depth: "about 4.5\" down from your waistband" }
};

  // ---------- data (inches) ----------
  // sizes: waist range used only to find the customer's starting size.
  // fits[line][size]: { waistDoubled, thighDoubled } — the two flat/doubled
  // garment specs everything else is computed from.
  const BOXER_BRIEF = {
    label: "Boxer Briefs",
    slug: "mens-premium-boxer-briefs",
    sizes: {
      XS: WR(25, 27), S: WR(28, 30), M: WR(31, 33),
      L: WR(34, 37), XL: WR(38, 41), XXL: WR(42, 45)
    },
    fits: {
      Modern: {
        XS: { waistDoubled: 21, thighDoubled: 12.5 },
        S:  { waistDoubled: 24, thighDoubled: 13.5 },
        M:  { waistDoubled: 27, thighDoubled: 14.5 },
        L:  { waistDoubled: 30, thighDoubled: 15.5 },
        XL: { waistDoubled: 33, thighDoubled: 16.5 },
        XXL:{ waistDoubled: 36, thighDoubled: 17.5 }
      },
      Athletic: {
        // XS, XXL not offered
        S:  { waistDoubled: 24, thighDoubled: 14.5 },
        M:  { waistDoubled: 27, thighDoubled: 15.5 },
        L:  { waistDoubled: 30, thighDoubled: 16.5 },
        XL: { waistDoubled: 33, thighDoubled: 17.5 }
      },
      EDAthletic: {}, // Phase 1 — TODO
      EDQuad: {},     // Phase 1 — TODO
      EDModern: {}    // Phase 2 — TODO
    }
  };

  const TRUNKS = {
    label: "Trunks",
    slug: "alphx-comfort-class-trunks",
    sizes: {
      XS: WR(25, 27), S: WR(28, 30), M: WR(31, 33),
      L: WR(34, 37), XL: WR(38, 41), XXL: WR(42, 45)
    },
    fits: {
      Modern: {
        XS: { waistDoubled: 21, thighDoubled: 15.5 },
        S:  { waistDoubled: 24, thighDoubled: 16.5 },
        M:  { waistDoubled: 27, thighDoubled: 17.5 },
        L:  { waistDoubled: 30, thighDoubled: 18.5 },
        XL: { waistDoubled: 33, thighDoubled: 19.5 },
        XXL:{ waistDoubled: 36, thighDoubled: 20.5 }
      },
      Athletic: {
        // XS, XXL not offered
        S:  { waistDoubled: 24, thighDoubled: 17.5 },
        M:  { waistDoubled: 27, thighDoubled: 18.5 },
        L:  { waistDoubled: 30, thighDoubled: 19.5 },
        XL: { waistDoubled: 33, thighDoubled: 20.5 }
      },
      EDAthletic: {}, // Phase 1 — TODO
      EDQuad: {},     // Phase 1 — TODO
      EDModern: {}    // Phase 2 — TODO
    }
  };

  const BRIEFS = {
    label: "Briefs",
    slug: "modern-briefs",
    sizes: {
      XS: WR(25, 27), S: WR(28, 30), M: WR(31, 33),
      L: WR(34, 37), XL: WR(38, 41), XXL: WR(42, 45)
    },
    fits: {
      // Modern only — Briefs does not have an Athletic line.
      Modern: {
        XS: { waistDoubled: 21, thighDoubled: 17 },
        S:  { waistDoubled: 24, thighDoubled: 18.5 },
        M:  { waistDoubled: 27, thighDoubled: 20 },
        L:  { waistDoubled: 30, thighDoubled: 21.5 },
        XL: { waistDoubled: 33, thighDoubled: 23 },
        XXL:{ waistDoubled: 36, thighDoubled: 24.5 }
      },
      EDAthletic: {}, // Phase 1 — TODO (confirm whether Briefs gets this line at all)
      EDQuad: {},     // Phase 1 — TODO (confirm whether Briefs gets this line at all)
      EDModern: {}    // Phase 2 — TODO (confirm whether Briefs gets this line at all)
    }
  };
const UNION_PANTS = {
  label: "Union Pants",
  slug: "alphx-union-pants",
  sizes: BOXER_BRIEF.sizes,
  fits: BOXER_BRIEF.fits
};
const CATALOG = [BOXER_BRIEF, TRUNKS, BRIEFS, UNION_PANTS];

  // ---------- core logic ----------
  function baseSizeByWaist(style, waist) {
    for (const size of ORDER) {
      const wr = style.sizes[size];
      if (wr && inRange(waist, wr.min, wr.max)) return size;
    }
    return null;
  }

  function activeFitNames(style) {
    return PHASE_LINES[ACTIVE_PHASE].filter(name => {
      const fit = style.fits[name];
      return fit && Object.keys(fit).length > 0;
    });
  }

  function evaluateCombo(style, fitName, size, waist, thigh) {
    const spec = style.fits[fitName] && style.fits[fitName][size];
    if (!spec) return null;

    const waistPct = stretchPercent(waist, spec.waistDoubled);
    const thighPct = stretchPercent(thigh, spec.thighDoubled);
    const waistTier = classify(waistPct, WAIST_BANDS);
    const thighTier = classify(thighPct, THIGH_BANDS);

    return {
      size, fit: fitName,
      waistPct, thighPct, waistTier, thighTier,
      wearable: waistTier !== "max" && thighTier !== "max",
      thighDistance: distanceToOptimal(thighPct, THIGH_BANDS),
      waistDistance: distanceToOptimal(waistPct, WAIST_BANDS)
    };
  }

  /** Best wearable combo in a list, ranked by closeness to the Optimal thigh band. */
  function bestOf(combos) {
    const wearable = combos.filter(c => c.wearable);
    if (!wearable.length) return null;
    wearable.sort((a, b) => a.thighDistance - b.thighDistance || a.waistDistance - b.waistDistance);
    return wearable[0];
  }

  /**
   * Waist size is a fact (it's just which band the customer's waist falls
   * in). Thigh fit is a judgment call — so this only ever moves the
   * customer OFF their true waist size when that size reads Secure (tight)
   * through the thigh, and only ever moves them UP, never down. A Relaxed
   * or Optimal thigh reading at the correct waist size is accepted as-is;
   * sizing down to chase a marginally closer Optimal reading would just
   * trade a fine outcome for a tighter waist, which is the opposite of
   * what "runs small" feedback is asking us to fix.
   */
  function recommend(style, waist, thigh) {
    const baseSize = baseSizeByWaist(style, waist);
    if (!baseSize) return null;

    const fitNames = activeFitNames(style);
    const baseCombos = fitNames.map(f => evaluateCombo(style, f, baseSize, waist, thigh)).filter(Boolean);
    const baseBest = bestOf(baseCombos);

    if (baseBest && (baseBest.thighTier === "optimal" || baseBest.thighTier === "relaxed")) {
      return toResult(style, baseBest, baseSize);
    }

    const upSize = sizeUp(baseSize);
    const upCombos = upSize ? fitNames.map(f => evaluateCombo(style, f, upSize, waist, thigh)).filter(Boolean) : [];
    const upBest = bestOf(upCombos);

    if (upBest && (!baseBest || upBest.thighDistance < baseBest.thighDistance)) {
      return toResult(style, upBest, baseSize);
    }
    if (baseBest) return toResult(style, baseBest, baseSize);
    if (upBest) return toResult(style, upBest, baseSize);

    return {
      style: style.label,
      size: null,
      note: "Your proportions fall outside our current lineup — we're building fits for exactly this. Reach out and we'll help you directly."
    };
  }

  function toResult(style, best, baseSize) {
    return {
      style: style.label,
      size: best.size,
      fit: best.fit,
      thighFeel: best.thighTier,
      waistFeel: best.waistTier,
      baseSize,
      note: buildNote(best, baseSize)
    };
  }

  function buildNote(best, baseSize) {
    const thighPhrase = {
      optimal: "Optimal fit through the thigh.",
      relaxed: "Relaxed fit through the thigh.",
      secure: "Secure fit through the thigh — our snuggest read for this size."
    }[best.thighTier];

    const parts = [];
    if (best.size !== baseSize) {
      parts.push(`Your true waist size is ${baseSize}, but ${best.size} (${best.fit}) gives a better fit through the thigh.`);
    }
    parts.push(thighPhrase);
    if (best.waistTier !== "optimal") {
      parts.push(`Waist runs ${best.waistTier}.`);
    }
    return parts.join(" ");
  }

  // ---------- UI ----------
  const mount = document.getElementById("fit-questionnaire");
  if (!mount) return;

  mount.innerHTML = `
    <label for="q-style">Which style are you shopping for?</label>
    <select id="q-style">
      <option value="">Select...</option>
      ${CATALOG.map(s => `<option value="${s.label}">${s.label}</option>`).join("")}
    </select>

    <label for="q-waist">Your waist size (inches)</label>
    <select id="q-waist">
      <option value="">Select...</option>
      ${Array.from({ length: 21 }, (_, i) => 25 + i).map(n => `<option>${n}</option>`).join("")}
    </select>

    <label for="q-thigh" id="q-thigh-label">Upper thigh circumference (inches)</label>
    <p id="q-thigh-hint" class="alphx-fit-hint">Choose a style above to see exactly where to measure.</p>
    <select id="q-thigh">
      <option value="">Select...</option>
      ${Array.from({ length: 31 }, (_, i) => 14 + i).map(n => `<option>${n}</option>`).join("")}
    </select>

    <button id="q-submit" type="button">GET MY FIT</button>
  `;

  const getVal = id => document.getElementById(id).value;
  const setText = (id, t) => (document.getElementById(id).textContent = t);

  const loading = document.getElementById("fit-loading");
  const result  = document.getElementById("fit-result");
  const link    = document.getElementById("shop-link");

  // Thigh guidance changes with the selected style — see MEASURE_GUIDE above.
  document.getElementById("q-style").addEventListener("change", (evt) => {
    const guide = MEASURE_GUIDE[evt.target.value];
    setText("q-thigh-label", evt.target.value
      ? `Upper thigh circumference for ${evt.target.value} (inches)`
      : "Upper thigh circumference (inches)");
    setText("q-thigh-hint", guide
      ? `${guide.method}, ${guide.depth}.`
      : "Choose a style above to see exactly where to measure.");
  });

  document.getElementById("q-submit").addEventListener("click", () => {
    const styleLabel = getVal("q-style");
    const waist = parseFloat(getVal("q-waist"));
    const thigh = parseFloat(getVal("q-thigh"));
    if (!styleLabel || isNaN(waist) || isNaN(thigh)) return;

    loading.style.display = "block";
    result.style.display  = "none";

    setTimeout(() => {
      const style = CATALOG.find(s => s.label === styleLabel);
      const rec = recommend(style, waist, thigh);

      loading.style.display = "none";
      result.style.display  = "block";

      if (!rec || !rec.size) {
        setText("fit-type", "We couldn't find a perfect match in that exact size.");
        setText("fit-style", (rec && rec.note) || "Try the next size up for more thigh room.");
        link.textContent = `Shop All ${style.label}`;
        link.href = `/collections/${style.slug}`;
        return;
      }

      setText("fit-type", `${style.label}: ${rec.fit} Fit`);
      setText("fit-style", `Recommended size: ${rec.size} — ${rec.note}`);
      link.textContent = `Shop ${style.label} in ${rec.size}`;
      link.href = `/collections/${style.slug}`;
    }, 200);
  });

  // Exposed for testing/reuse (e.g. the fit-authority page) — not required by the UI above.
  window.AlphxFit = { recommend, CATALOG, ACTIVE_PHASE, PHASE_LINES, classify, stretchPercent };

  // version flag
  window.__FITMATRIX_VERSION__ = "2026-07-22b";
  console.log("ALPHX fitMatrix loaded:", window.__FITMATRIX_VERSION__);
})();